import {ref, computed, reactive} from 'vue';
import {quoteEndpoints, clientAccountEndpoints} from '@registry';
import {and, applyIf, checked, required, withMessage} from '@regle/rules';
import {defineStore} from 'pinia';
import {useAuth, useTranslation} from '#imports';
import {useRegle} from '@malt/forms';
import {useErrorHandler} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {
    FixedPriceQuoteResource,
    TimeAndMaterialsQuoteResource,
    AcceptQuoteResource,
    DeclineQuoteRequestReasonEnum,
    SetPurchaseOrderToQuoteRequest,
    PreAcceptQuoteRequest,
    QuoteManagerUpdateRequest,
    SendQuoteToErpResponse,
} from '@api';
import {uploadFiles} from '~/common/utils';
import {isQuoteFixedPrice, isQuoteTimeAndMaterials} from '../utils';

export const useClientQuoteViewStore = defineStore('client-quote-view', () => {
    const {t} = useTranslation();
    const {handleUnknownError} = useErrorHandler();
    const authStore = useAuth();

    const quote = ref<FixedPriceQuoteResource | TimeAndMaterialsQuoteResource>();

    const isClientQuoteOwner = computed(() => {
        return authStore.user?.selectedIdentity === quote.value?.client.identityId;
    });

    const isFeesInfoVisible = computed(() => !!quote.value?.options.shouldDisplayFees);
    const showAddPhoneNumber = computed(() => !!quote.value?.options.needClientPhoneNumber);
    const isPunchOutEnabled = computed(() => quote.value?.options.punchout.enabled);

    const state = reactive({acceptedProjectTerms: false});
    const rules = {
        acceptedProjectTerms: {
            $rewardEarly: true,
            checked: withMessage(
                applyIf(
                    () => {
                        if (quote.value?.projectTerms) {
                            return true;
                        }
                        return false;
                    },
                    and(checked, required),
                ),
                t('projectclient.quote.view.terms-and-conditions.project.terms.client.not.accepted.required'),
            ),
        },
    };

    const {r$} = useRegle(state, rules);

    async function getQuote(quoteId: string) {
        try {
            const data = await quoteEndpoints.getQuote({quoteId});

            if (isQuoteFixedPrice(data) || isQuoteTimeAndMaterials(data)) {
                quote.value = data;
            }
        } catch (e) {
            handleUnknownError(e);
        }
    }

    async function fetchQuoteByCorrelationId(correlationId: string) {
        try {
            const data = await quoteEndpoints.getQuoteByCorrelationId({correlationId});

            if (isQuoteFixedPrice(data) || isQuoteTimeAndMaterials(data)) {
                quote.value = data;
            }
        } catch (e) {
            handleUnknownError(e);
        }
    }

    async function setPurchaseOrder(requestParameters: SetPurchaseOrderToQuoteRequest): Promise<'applied' | 'error'> {
        try {
            await quoteEndpoints.setPurchaseOrderToQuote(requestParameters);
            return 'applied';
        } catch (e) {
            handleUnknownError(e);
            return 'error';
        }
    }

    async function setAccountPhoneNumber(accountId: string, phoneNumber: string) {
        await clientAccountEndpoints.updateAccountPhoneNumber({
            accountId,
            updatePhoneNumberRequest: {
                phoneNumber,
            },
        });
    }

    async function acceptQuote(quoteId: string): Promise<AcceptQuoteResource | undefined> {
        const {valid} = await r$.$validate();
        if (valid) {
            try {
                return await quoteEndpoints.acceptQuote({quoteId});
            } catch (e) {
                handleUnknownError(e);
                return undefined;
            }
        }
    }

    async function declineQuote(quoteId: string, reason: DeclineQuoteRequestReasonEnum, message: string) {
        try {
            await quoteEndpoints.declineQuote({quoteId, declineQuoteRequest: {reason, message}});
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.quote.view.quote-summary.dialog.decline.confirm.success.header'),
                    message: t('projectclient.quote.view.quote-summary.dialog.decline.confirm.success.message'),
                    level: 'success',
                },
            });
        } catch (e) {
            handleUnknownError(e);
        }
    }

    async function preAcceptQuote(quoteId: string, clientAccountIds: string[]) {
        const preAcceptQuoteRequest = {
            clientAdminAccountIds: clientAccountIds,
        } as PreAcceptQuoteRequest;

        try {
            await quoteEndpoints.preAcceptQuote({quoteId, preAcceptQuoteRequest});
        } catch (e) {
            handleUnknownError(e);
            return undefined;
        }
    }

    function constructMessageAndHeader(updateType: QuoteManagerUpdateRequest, newManagerName?: string) {
        let message = '';
        let header = '';

        if (updateType.newIdentityId) {
            header = t('projectclient.quote.view.project-team.dialog.project-owner.notification.selection.success.header');
            message = t('projectclient.quote.view.project-team.dialog.project-owner.notification.selection.success.message', {0: newManagerName});
        } else if (updateType.invitationEmail) {
            header = t('projectclient.quote.view.project-team.dialog.project-owner.notification.email.success.header');
            message = t('projectclient.quote.view.project-team.dialog.project-owner.notification.email.success.message', {
                0: updateType.invitationEmail,
            });
        }

        return {message, header};
    }

    async function updateQuoteOwner({
        invitationEmail,
        newIdentityId,
        quoteId,
        newManagerName,
    }: {
        quoteId: string;
        newIdentityId?: string;
        invitationEmail?: string;
        newManagerName?: string;
    }) {
        const quoteManagerUpdateRequest = {
            newIdentityId,
            invitationEmail,
        } satisfies QuoteManagerUpdateRequest;
        try {
            await quoteEndpoints.updateQuoteManager({quoteId, quoteManagerUpdateRequest});
            const {message, header} = constructMessageAndHeader(quoteManagerUpdateRequest, newManagerName);

            pushVJoySnackbar({
                props: {
                    message,
                    header,
                    level: 'success',
                },
            });
        } catch (e) {
            handleUnknownError(e);
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.quote.view.project-team.dialog.project-owner.notification.error.header'),
                    message: t('projectclient.quote.view.project-team.dialog.project-owner.notification.error.message'),
                    level: 'error',
                },
            });
            return undefined;
        }
    }

    async function sendQuoteToErp(quoteId: string): Promise<SendQuoteToErpResponse | undefined> {
        try {
            return await quoteEndpoints.sendQuoteToErp({quoteId});
        } catch (e) {
            handleUnknownError(e);
            return undefined;
        }
    }

    async function uploadAppendixFile(quoteId: string, appendixFile: File) {
        try {
            const [fileId] = await uploadFiles([appendixFile]);

            await quoteEndpoints.updateQuoteAppendix({
                quoteId,
                updateQuoteAppendixRequest: {
                    fileId,
                    fileName: appendixFile.name,
                },
            });

            pushVJoySnackbar({
                props: {
                    message: t('projectclient.quote.view.appendix.action.upload.saved'),
                    level: 'success',
                },
            });
        } catch (e) {
            handleUnknownError(e);
            return undefined;
        }
    }

    async function deleteAppendixFile(quoteId: string) {
        try {
            await quoteEndpoints.deleteQuoteAppendix({
                quoteId,
            });

            pushVJoySnackbar({
                props: {
                    message: t('projectclient.quote.view.appendix.action.upload.deleted'),
                    level: 'success',
                },
            });
        } catch (e) {
            handleUnknownError(e);
            return undefined;
        }
    }

    return {
        r$,
        quote,
        getQuote,
        fetchQuoteByCorrelationId,
        acceptQuote,
        isClientQuoteOwner,
        isFeesInfoVisible,
        declineQuote,
        setPurchaseOrder,
        setAccountPhoneNumber,
        preAcceptQuote,
        updateQuoteOwner,
        showAddPhoneNumber,
        isPunchOutEnabled,
        sendQuoteToErp,
        uploadAppendixFile,
        deleteAppendixFile,
    };
});
